import { ModelViewerElement } from '@google/model-viewer';
import { Box, IconButton } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { ARIcon } from '../../../../assets';
import { openAR } from '../../../../utils';
import { IS_REACT_NATIVE } from '../../../../utils/constants';
import { Model3DProps } from '../../types';
import { toAbsolutePath } from '../../utils';
import { useStyles } from './styles';

export const Model3D: React.FC<Model3DProps> = ({ src, srcIos, alt, title, arButtonKey }) => {
  const styles = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const modelRef = useRef<ModelViewerElement>();

  const onARClick = () => {
    openAR({
      title,
      sources: {
        src: toAbsolutePath(src),
        srcIos: toAbsolutePath(srcIos),
      },
    });
  };

  useEffect(() => {
    const el = containerRef.current;

    el?.addEventListener(
      'touchmove',
      e => {
        // this is used to prevent swiping while manipulating the 3D model
        e.stopPropagation();
      },
      { passive: false },
    );

    return () => {
      if (el) {
        el.ontouchmove = null;
      }
    };
  }, []);

  useEffect(() => {
    const el = modelRef.current;
    if (!el) {
      return;
    }

    const onLoad = () => {
      const material = el.model?.materials?.[0];
      // https://pizzahutinternational.atlassian.net/browse/HBAR-55
      material?.setAlphaMode('MASK');
    };

    el.addEventListener('load', onLoad);

    return () => {
      el.removeEventListener('load', onLoad);
    };
  }, []);

  return (
    <Box ref={containerRef} className={styles.container}>
      <model-viewer
        ref={modelRef}
        class={styles.model}
        src={src}
        ios-src={srcIos}
        alt={alt || title}
        camera-controls
        ar
        shadow-intensity="0.5"
        ar-modes="scene-viewer quick-look webxr"
        ar-placement="floor">
        <slot name="ar-button">
          {IS_REACT_NATIVE && window.mapp?.ar?.supported && (
            <IconButton className={styles.button} onClick={onARClick} key={arButtonKey}>
              <ARIcon />
            </IconButton>
          )}
        </slot>
      </model-viewer>
    </Box>
  );
};
